/**
 * All The Horses - Theme JavaScript
 * Vanilla JS with no dependencies
 */

(function() {
  'use strict';

  /* ============================================
     Utility Functions
     ============================================ */

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  const debounce = (fn, delay = 150) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const throttle = (fn, limit = 150) => {
    let inThrottle;
    return (...args) => {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // Trap focus within element (for modals/drawers)
  const trapFocus = (element) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  };

  /* ============================================
     Header Component
     ============================================ */

  class Header {
    constructor() {
      this.header = $('.site-header');
      if (!this.header) return;

      this.mobileToggle = $('.mobile-menu-toggle');
      this.mobileNav = $('.mobile-nav');
      this.mobileNavClose = $('.mobile-nav-close');

      this.lastScrollY = 0;
      this.scrollThreshold = 100;

      this.init();
    }

    init() {
      this.bindEvents();
    }

    bindEvents() {
      // Mobile menu toggle
      if (this.mobileToggle && this.mobileNav) {
        this.mobileToggle.addEventListener('click', () => this.toggleMobileMenu());

        if (this.mobileNavClose) {
          this.mobileNavClose.addEventListener('click', () => this.closeMobileMenu());
        }

        // Close on escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.mobileNav.classList.contains('is-open')) {
            this.closeMobileMenu();
          }
        });
      }

      // Scroll behavior
      window.addEventListener('scroll', throttle(() => this.handleScroll(), 100));
    }

    toggleMobileMenu() {
      const isOpen = this.mobileNav.classList.toggle('is-open');
      this.mobileToggle.classList.toggle('is-active', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';

      if (isOpen) {
        this.mobileToggle.setAttribute('aria-expanded', 'true');
        trapFocus(this.mobileNav);
      } else {
        this.mobileToggle.setAttribute('aria-expanded', 'false');
      }
    }

    closeMobileMenu() {
      this.mobileNav.classList.remove('is-open');
      this.mobileToggle.classList.remove('is-active');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    handleScroll() {
      const currentScrollY = window.scrollY;

      // Add scrolled class
      if (currentScrollY > 10) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      // Hide/show on scroll direction
      if (currentScrollY > this.scrollThreshold) {
        if (currentScrollY > this.lastScrollY) {
          this.header.classList.add('is-hidden');
        } else {
          this.header.classList.remove('is-hidden');
        }
      }

      this.lastScrollY = currentScrollY;
    }
  }

  /* ============================================
     Accordion Component
     ============================================ */

  class Accordion {
    constructor(element) {
      this.element = element;
      this.items = $$('.accordion-item', element);
      this.allowMultiple = element.dataset.allowMultiple === 'true';

      this.init();
    }

    init() {
      this.items.forEach(item => {
        const trigger = $('.accordion-trigger', item);
        if (trigger) {
          trigger.addEventListener('click', () => this.toggle(item));
        }
      });
    }

    toggle(item) {
      const isOpen = item.classList.contains('is-open');

      if (!this.allowMultiple) {
        this.items.forEach(i => i.classList.remove('is-open'));
      }

      item.classList.toggle('is-open', !isOpen);
    }
  }

  /* ============================================
     Tabs Component
     ============================================ */

  class Tabs {
    constructor(element) {
      this.element = element;
      this.triggers = $$('.tab-trigger', element);
      this.panels = $$('.tab-panel', element);

      this.init();
    }

    init() {
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.activate(index));
      });
    }

    activate(index) {
      this.triggers.forEach(t => t.classList.remove('is-active'));
      this.panels.forEach(p => p.classList.remove('is-active'));

      this.triggers[index].classList.add('is-active');
      this.panels[index].classList.add('is-active');
    }
  }

  /* ============================================
     Scroll Reveal
     ============================================ */

  class ScrollReveal {
    constructor() {
      this.elements = $$('[data-reveal]');
      if (!this.elements.length) return;

      this.init();
    }

    init() {
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.elements.forEach(el => el.classList.add('is-revealed'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      this.elements.forEach(el => observer.observe(el));
    }
  }

  /* ============================================
     Quantity Selector
     ============================================ */

  class QuantitySelector {
    constructor(element) {
      this.element = element;
      this.input = $('input', element);
      this.decrementBtn = $('[data-decrement]', element);
      this.incrementBtn = $('[data-increment]', element);

      if (!this.input) return;

      this.min = parseInt(this.input.min) || 1;
      this.max = parseInt(this.input.max) || 9999;

      this.init();
    }

    init() {
      if (this.decrementBtn) {
        this.decrementBtn.addEventListener('click', () => this.decrement());
      }
      if (this.incrementBtn) {
        this.incrementBtn.addEventListener('click', () => this.increment());
      }

      this.input.addEventListener('change', () => this.validate());
    }

    decrement() {
      const currentValue = parseInt(this.input.value) || 1;
      if (currentValue > this.min) {
        this.input.value = currentValue - 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    increment() {
      const currentValue = parseInt(this.input.value) || 1;
      if (currentValue < this.max) {
        this.input.value = currentValue + 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    validate() {
      let value = parseInt(this.input.value) || this.min;
      value = Math.max(this.min, Math.min(this.max, value));
      this.input.value = value;
    }
  }

  /* ============================================
     Drawer Component
     ============================================ */

  class Drawer {
    constructor(element) {
      this.drawer = element;
      this.backdrop = $('.drawer-backdrop[data-drawer="' + element.id + '"]');
      this.closeBtn = $('.drawer-close', element);
      this.triggers = $$('[data-drawer-open="' + element.id + '"]');

      this.init();
    }

    init() {
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    open() {
      this.drawer.classList.add('is-open');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      trapFocus(this.drawer);
    }

    close() {
      this.drawer.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  /* ============================================
     Image Lazy Loading
     ============================================ */

  class LazyImages {
    constructor() {
      this.images = $$('img[loading="lazy"]');
      if (!this.images.length) return;

      this.init();
    }

    init() {
      this.images.forEach(img => {
        if (img.complete) {
          this.handleLoaded(img);
        } else {
          img.addEventListener('load', () => this.handleLoaded(img));
        }
      });
    }

    handleLoaded(img) {
      const container = img.closest('.image-loading');
      if (container) {
        container.classList.add('is-loaded');
      }
    }
  }

  /* ============================================
     Product Form
     ============================================ */

  class ProductForm {
    constructor(element) {
      this.form = element;
      this.variantSelects = $$('select[name="id"], input[name="id"]', element);
      this.quantityInput = $('input[name="quantity"]', element);
      this.submitButton = $('[type="submit"]', element);

      this.init();
    }

    init() {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    async handleSubmit(e) {
      e.preventDefault();

      const formData = new FormData(this.form);

      // Disable button during submission
      this.submitButton.disabled = true;
      const originalText = this.submitButton.textContent;
      this.submitButton.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const data = await response.json();

        // Klaviyo: Track Add to Cart event
        if (window.ATH && window.ATH.KlaviyoAPI && window.ATH.klaviyoPublicKey) {
          var email = window.ATH._klaviyoEmail;
          if (email) {
            ATH.KlaviyoAPI.trackEvent({
              email: email,
              eventName: 'ATH Add to Cart',
              properties: {
                product_title: data.product_title || data.title,
                variant_title: data.variant_title,
                price: data.price,
                quantity: data.quantity,
                url: window.location.href
              }
            });
          }
        }

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        this.submitButton.textContent = 'Added!';
        setTimeout(() => {
          this.submitButton.textContent = originalText;
          this.submitButton.disabled = false;
        }, 1500);

      } catch (error) {
        console.error('Add to cart error:', error);
        this.submitButton.textContent = 'Error';
        setTimeout(() => {
          this.submitButton.textContent = originalText;
          this.submitButton.disabled = false;
        }, 1500);
      }
    }
  }

  /* ============================================
     Cart API Helper
     ============================================ */

  const CartAPI = {
    async get() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    async add(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      return response.json();
    },

    async update(updates) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return response.json();
    },

    async changeQuantity(key, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      return response.json();
    },

    async clear() {
      const response = await fetch('/cart/clear.js', {
        method: 'POST'
      });
      return response.json();
    }
  };

  // Expose CartAPI globally
  window.ATH = window.ATH || {};
  window.ATH.CartAPI = CartAPI;

  /* ============================================
     Klaviyo Event Tracking
     ============================================ */

  const initKlaviyoTracking = () => {
    if (!window.ATH || !window.ATH.KlaviyoAPI || !window.ATH.klaviyoPublicKey) return;

    // Track Product Viewed on product pages
    const productMeta = document.querySelector('[data-product-json]');
    if (productMeta && document.body.classList.contains('template-product')) {
      try {
        const product = JSON.parse(productMeta.textContent);
        const email = window.ATH._klaviyoEmail;
        if (email && product) {
          ATH.KlaviyoAPI.trackEvent({
            email: email,
            eventName: 'ATH Product Viewed',
            properties: {
              product_title: product.title,
              product_id: product.id,
              handle: product.handle,
              price: product.price,
              vendor: product.vendor,
              url: window.location.href
            }
          });
        }
      } catch (e) {}
    }

    // Track Collection Viewed on collection pages
    if (document.body.classList.contains('template-collection')) {
      const email = window.ATH._klaviyoEmail;
      var heading = document.querySelector('.collection-header h1, .collection-title');
      if (email) {
        ATH.KlaviyoAPI.trackEvent({
          email: email,
          eventName: 'ATH Collection Viewed',
          properties: {
            collection_title: heading ? heading.textContent.trim() : '',
            url: window.location.href
          }
        });
      }
    }
  };

  /* ============================================
     Initialize Components
     ============================================ */

  const initComponents = () => {
    // Header
    new Header();

    // Scroll reveal
    new ScrollReveal();

    // Lazy images
    new LazyImages();

    // Accordions
    $$('.accordion').forEach(el => new Accordion(el));

    // Tabs
    $$('.tabs').forEach(el => new Tabs(el));

    // Quantity selectors
    $$('.quantity-selector').forEach(el => new QuantitySelector(el));

    // Drawers
    $$('.drawer').forEach(el => new Drawer(el));

    // Product forms
    $$('form[action="/cart/add"]').forEach(el => new ProductForm(el));

    // Klaviyo event tracking
    initKlaviyoTracking();
  };

  /* ============================================
     DOM Ready
     ============================================ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initComponents);
  } else {
    initComponents();
  }

})();
